import { encodeFunctionData, getAddress } from "viem";
import { GUARDIAN_REGISTRY_ABI } from "../abis.js";
import { getDeployment } from "../addresses.js";
import { unsupported, usage } from "../errors.js";
const ZERO_VALUE = "0x0";
function registryTx(chainId, data) {
    const registry = getDeployment(chainId).sherwood.guardianRegistry;
    if (registry === "0x0000000000000000000000000000000000000000") {
        throw unsupported(`Guardian registry is not deployed on chain ${chainId} yet`);
    }
    return { to: registry, data, value: ZERO_VALUE, chainId };
}
function parseUint(value, field) {
    if (typeof value === "bigint") {
        if (value < 0n)
            throw usage(`\`${field}\` must be a non-negative integer`);
        return value;
    }
    if (typeof value === "number") {
        if (!Number.isInteger(value) || value < 0) {
            throw usage(`\`${field}\` must be a non-negative integer`);
        }
        return BigInt(value);
    }
    if (!/^\d+$/.test(value)) {
        throw usage(`\`${field}\` must be a non-negative integer string, got "${value}"`);
    }
    return BigInt(value);
}
// ── Guardian self-stake ───────────────────────────────────
export function encodeGuardianStake(args, chainId) {
    if (args.amount <= 0n)
        throw usage("`amount` must be > 0");
    const agentId = parseUint(args.agentId, "agentId");
    const data = encodeFunctionData({
        abi: GUARDIAN_REGISTRY_ABI,
        functionName: "stakeAsGuardian",
        args: [args.amount, agentId],
    });
    const registry = getDeployment(chainId).sherwood.guardianRegistry;
    return {
        txs: [registryTx(chainId, data)],
        preconditions: [
            // Caller must have approved WOOD for the registry; agent should
            // check this client-side and prepend an ERC20.approve if needed.
            {
                type: "allowance",
                token: registry,
                spender: registry,
                min: args.amount.toString(),
                minDecimal: args.amount.toString(),
            },
        ],
        description: `Stake ${args.amount.toString()} WOOD as guardian (agent ${agentId.toString()}).`,
        note: "Caller must have approved WOOD for the registry first. Stake locks the WOOD; use /prepare/guardian-unstake later to start the 7d unstake cooldown.",
    };
}
const GUARDIAN_UNSTAKE_FN = {
    request: "requestUnstakeGuardian",
    cancel: "cancelUnstakeGuardian",
    claim: "claimUnstakeGuardian",
};
export function encodeGuardianUnstake(args, chainId) {
    const fn = GUARDIAN_UNSTAKE_FN[args.action];
    if (!fn) {
        throw usage(`\`action\` must be one of: ${Object.keys(GUARDIAN_UNSTAKE_FN).join(", ")}`);
    }
    const data = encodeFunctionData({
        abi: GUARDIAN_REGISTRY_ABI,
        functionName: fn,
        args: [],
    });
    return {
        txs: [registryTx(chainId, data)],
        preconditions: [],
        description: `Guardian unstake ${args.action}.`,
        note: args.action === "request"
            ? "Starts a 7-day cooldown. After it elapses, call again with action=claim to withdraw."
            : args.action === "claim"
                ? "Withdraws the unstake amount once the 7-day cooldown has elapsed."
                : "Cancels a pending unstake request and re-activates the guardian.",
    };
}
// ── Delegation ────────────────────────────────────────────
export function encodeGuardianDelegate(args, chainId) {
    if (args.amount <= 0n)
        throw usage("`amount` must be > 0");
    const delegate = getAddress(args.delegate);
    const data = encodeFunctionData({
        abi: GUARDIAN_REGISTRY_ABI,
        functionName: "delegateStake",
        args: [delegate, args.amount],
    });
    return {
        txs: [registryTx(chainId, data)],
        preconditions: [],
        description: `Delegate ${args.amount.toString()} WOOD to ${delegate}.`,
        note: "Caller must have approved WOOD for the registry first. Delegation does NOT activate the delegate as a guardian — they must self-stake separately.",
    };
}
const DELEGATION_UNSTAKE_FN = {
    request: "requestUnstakeDelegation",
    cancel: "cancelUnstakeDelegation",
    claim: "claimUnstakeDelegation",
};
export function encodeDelegationUnstake(args, chainId) {
    const fn = DELEGATION_UNSTAKE_FN[args.action];
    if (!fn) {
        throw usage(`\`action\` must be one of: ${Object.keys(DELEGATION_UNSTAKE_FN).join(", ")}`);
    }
    const delegate = getAddress(args.delegate);
    const data = encodeFunctionData({
        abi: GUARDIAN_REGISTRY_ABI,
        functionName: fn,
        args: [delegate],
    });
    return {
        txs: [registryTx(chainId, data)],
        preconditions: [],
        description: `Delegation unstake ${args.action} for delegate ${delegate}.`,
        note: args.action === "request"
            ? "Starts a 7-day cooldown on the delegated amount."
            : args.action === "claim"
                ? "Withdraws the delegated amount once the 7-day cooldown has elapsed."
                : "Cancels a pending unstake of the delegation.",
    };
}
// ── Commission ────────────────────────────────────────────
export function encodeSetCommission(args, chainId) {
    if (!Number.isInteger(args.bps) || args.bps < 0 || args.bps > 5000) {
        throw usage("`bps` must be an integer in [0, 5000] (0–50%)");
    }
    const data = encodeFunctionData({
        abi: GUARDIAN_REGISTRY_ABI,
        functionName: "setCommission",
        args: [args.bps],
    });
    return {
        txs: [registryTx(chainId, data)],
        preconditions: [],
        description: `Set DPoS commission to ${args.bps} bps.`,
        note: "Raise-rate is capped at 500 bps above the epoch-start rate; first set on a delegate is exempt. Decreases are unbounded.",
    };
}
// Guardian-fee reward claims are no longer on-chain. Fees route to the
// `guardiansFeeRecipient` multisig at settlement and are distributed as WOOD
// off-chain via Merkl (claim at merkl.xyz) — the on-chain
// claimProposalReward / claimDelegatorProposalReward encoders were removed.
